/*
 * Product share button.
 *
 * Injected rather than authored into templates/product.json because the buy-buttons
 * group is a platform block: its children are rendered by content_for and cannot take
 * an extra child from the template without a custom block type.
 *
 * Uses the Web Share API where the browser has it (every mobile browser worth caring
 * about), and falls back to copying the link with visible confirmation. The icon and
 * the copy-with-feedback pattern are the same ones the saved-pieces dialog already
 * uses, so the two read as one feature.
 */
(() => {
  if (!/^\/products\//.test(window.location.pathname)) return;

  const SHARE_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M12 15V3m0 0L7.5 7.5M12 3l4.5 4.5"></path>' +
    '<path d="M7 10H5.8A2.8 2.8 0 0 0 3 12.8v5.4A2.8 2.8 0 0 0 5.8 21h12.4a2.8 2.8 0 0 0 2.8-2.8v-5.4a2.8 2.8 0 0 0-2.8-2.8H17"></path>' +
    '</svg>';

  const productTitle = () => {
    const h1 = document.querySelector('[data-testid="product-information-details"] h1, main h1');
    return (h1 && h1.textContent.trim()) || document.title;
  };

  const shareUrl = () => {
    const canonical = document.querySelector('link[rel="canonical"]');
    return (canonical && canonical.href) || window.location.href.split('?')[0];
  };

  const mount = () => {
    if (document.getElementById('namaslay-share')) return;

    // Sit with the reassurance items under the buy buttons, not among them.
    const anchor =
      document.querySelector('[data-testid="product-information-details"] .shopify-product-form') ||
      document.querySelector('[data-testid="product-information-details"] product-form-component');
    if (!anchor) return;

    const button = document.createElement('button');
    button.id = 'namaslay-share';
    button.className = 'namaslay-share';
    button.type = 'button';
    button.innerHTML = SHARE_SVG + '<span class="namaslay-share__label">Share this piece</span>';

    const setLabel = (text, transient) => {
      const label = button.querySelector('.namaslay-share__label');
      if (!label) return;
      label.textContent = text;
      if (!transient) return;
      button.classList.add('is-feedback');
      clearTimeout(button.__resetTimer);
      button.__resetTimer = setTimeout(() => {
        label.textContent = 'Share this piece';
        button.classList.remove('is-feedback');
      }, 2200);
    };

    button.addEventListener('click', async () => {
      const url = shareUrl();
      const title = productTitle();

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (error) {
          // A dismissed share sheet is not a failure; only fall through on a real one.
          if (error && error.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        setLabel('Link copied', true);
      } catch (_) {
        // Clipboard access is refused outside a secure context and in some in-app
        // browsers; select the URL so it can still be copied by hand.
        const field = document.createElement('input');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
        document.body.appendChild(field);
        field.select();
        let copied = false;
        try { copied = document.execCommand('copy'); } catch (__) {}
        field.remove();
        setLabel(copied ? 'Link copied' : 'Press ⌘C to copy', true);
      }
    });

    anchor.insertAdjacentElement('afterend', button);
  };

  const start = () => {
    mount();
    // The product form re-renders on variant change, which can drop the button.
    new MutationObserver(() => {
      clearTimeout(window.__namaslayShareTimer);
      window.__namaslayShareTimer = setTimeout(mount, 150);
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
